// ============================================================================
// BP-04-02: Documents Frontend - Zustand Store
// ============================================================================

import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import type {
  Document,
  DocumentFolder,
  DocumentFilters,
  UploadProgress,
  ViewMode,
  SortField,
  SortOrder,
} from '../types/documentsTypes';

// -----------------------------------------------------------------------------
// Store State Interface
// -----------------------------------------------------------------------------

interface DocumentsState {
  // View state
  viewMode: ViewMode;
  currentFolderId: string | null;
  
  // Selection
  selectedDocumentIds: string[];
  
  // Folder expansion (for tree view)
  expandedFolderIds: string[];
  
  // Filters
  filters: DocumentFilters;
  sortBy: SortField;
  sortOrder: SortOrder;
  
  // Upload queue
  uploadQueue: UploadProgress[];
  isUploading: boolean;
  
  // Modal states
  isViewerOpen: boolean;
  viewerDocumentId: string | null;
  isCreateFolderOpen: boolean;
  createFolderParentId: string | null;
  isMoveModalOpen: boolean;
  moveDocumentIds: string[];
  isShareModalOpen: boolean;
  shareDocumentId: string | null;
  
  // Cached data (for offline support)
  cachedFolders: DocumentFolder[];
  recentDocuments: Document[];
}

interface DocumentsActions {
  // View actions
  setViewMode: (mode: ViewMode) => void;
  setCurrentFolderId: (folderId: string | null) => void;
  
  // Selection actions
  selectDocument: (documentId: string) => void;
  deselectDocument: (documentId: string) => void;
  toggleDocumentSelection: (documentId: string) => void;
  selectAllDocuments: (documentIds: string[]) => void;
  clearSelection: () => void;
  
  // Folder expansion actions
  expandFolder: (folderId: string) => void;
  collapseFolder: (folderId: string) => void;
  toggleFolderExpansion: (folderId: string) => void;
  expandAllFolders: (folderIds: string[]) => void;
  collapseAllFolders: () => void;
  
  // Filter actions
  setFilters: (filters: Partial<DocumentFilters>) => void;
  clearFilters: () => void;
  setSortBy: (field: SortField) => void;
  setSortOrder: (order: SortOrder) => void;
  toggleSortOrder: () => void;
  
  // Upload actions
  addToUploadQueue: (file: File) => void;
  removeFromUploadQueue: (filename: string) => void;
  updateUploadProgress: (filename: string, progress: Partial<UploadProgress>) => void;
  clearUploadQueue: () => void;
  setIsUploading: (isUploading: boolean) => void;
  
  // Modal actions
  openViewer: (documentId: string) => void;
  closeViewer: () => void;
  openCreateFolder: (parentId?: string | null) => void;
  closeCreateFolder: () => void;
  openMoveModal: (documentIds: string[]) => void;
  closeMoveModal: () => void;
  openShareModal: (documentId: string) => void;
  closeShareModal: () => void;
  
  // Cache actions
  setCachedFolders: (folders: DocumentFolder[]) => void;
  setRecentDocuments: (documents: Document[]) => void;
  addToRecentDocuments: (document: Document) => void;
  
  // Reset
  reset: () => void;
}

// -----------------------------------------------------------------------------
// Initial State
// -----------------------------------------------------------------------------

const initialState: DocumentsState = {
  viewMode: 'grid' as ViewMode,
  currentFolderId: null,
  selectedDocumentIds: [],
  expandedFolderIds: [],
  filters: {},
  sortBy: 'updated_at' as SortField,
  sortOrder: 'desc' as SortOrder,
  uploadQueue: [],
  isUploading: false,
  isViewerOpen: false,
  viewerDocumentId: null,
  isCreateFolderOpen: false,
  createFolderParentId: null,
  isMoveModalOpen: false,
  moveDocumentIds: [],
  isShareModalOpen: false,
  shareDocumentId: null,
  cachedFolders: [],
  recentDocuments: [],
};

// -----------------------------------------------------------------------------
// Store
// -----------------------------------------------------------------------------

export const useDocumentsStore = create<DocumentsState & DocumentsActions>()(
  devtools(
    persist(
      (set, get) => ({
        ...initialState,

        // View actions
        setViewMode: (mode) => set({ viewMode: mode }),
        setCurrentFolderId: (folderId) => set({ currentFolderId: folderId }),

        // Selection actions
        selectDocument: (documentId) =>
          set((state) => ({
            selectedDocumentIds: state.selectedDocumentIds.includes(documentId)
              ? state.selectedDocumentIds
              : [...state.selectedDocumentIds, documentId],
          })),

        deselectDocument: (documentId) =>
          set((state) => ({
            selectedDocumentIds: state.selectedDocumentIds.filter((id) => id !== documentId),
          })),

        toggleDocumentSelection: (documentId) =>
          set((state) => ({
            selectedDocumentIds: state.selectedDocumentIds.includes(documentId)
              ? state.selectedDocumentIds.filter((id) => id !== documentId)
              : [...state.selectedDocumentIds, documentId],
          })),

        selectAllDocuments: (documentIds) => set({ selectedDocumentIds: documentIds }),

        clearSelection: () => set({ selectedDocumentIds: [] }),

        // Folder expansion actions
        expandFolder: (folderId) =>
          set((state) => ({
            expandedFolderIds: state.expandedFolderIds.includes(folderId)
              ? state.expandedFolderIds
              : [...state.expandedFolderIds, folderId],
          })),

        collapseFolder: (folderId) =>
          set((state) => ({
            expandedFolderIds: state.expandedFolderIds.filter((id) => id !== folderId),
          })),

        toggleFolderExpansion: (folderId) =>
          set((state) => ({
            expandedFolderIds: state.expandedFolderIds.includes(folderId)
              ? state.expandedFolderIds.filter((id) => id !== folderId)
              : [...state.expandedFolderIds, folderId],
          })),

        expandAllFolders: (folderIds) => set({ expandedFolderIds: folderIds }),

        collapseAllFolders: () => set({ expandedFolderIds: [] }),

        // Filter actions
        setFilters: (filters) =>
          set((state) => ({
            filters: { ...state.filters, ...filters },
          })),

        clearFilters: () => set({ filters: {} }),

        setSortBy: (field) => set({ sortBy: field }),

        setSortOrder: (order) => set({ sortOrder: order }),

        toggleSortOrder: () =>
          set((state) => ({
            sortOrder: state.sortOrder === 'asc' ? 'desc' : 'asc',
          })),

        // Upload actions
        addToUploadQueue: (file) =>
          set((state) => ({
            uploadQueue: [
              ...state.uploadQueue,
              {
                file,
                progress: 0,
                status: 'pending',
              },
            ],
          })),

        removeFromUploadQueue: (filename) =>
          set((state) => ({
            uploadQueue: state.uploadQueue.filter((item) => item.file.name !== filename),
          })),

        updateUploadProgress: (filename, progress) =>
          set((state) => ({
            uploadQueue: state.uploadQueue.map((item) =>
              item.file.name === filename ? { ...item, ...progress } : item
            ),
          })),

        clearUploadQueue: () => set({ uploadQueue: [], isUploading: false }),

        setIsUploading: (isUploading) => set({ isUploading }),

        // Modal actions
        openViewer: (documentId) =>
          set({ isViewerOpen: true, viewerDocumentId: documentId }),

        closeViewer: () => set({ isViewerOpen: false, viewerDocumentId: null }),

        openCreateFolder: (parentId = null) =>
          set({ isCreateFolderOpen: true, createFolderParentId: parentId }),

        closeCreateFolder: () =>
          set({ isCreateFolderOpen: false, createFolderParentId: null }),

        openMoveModal: (documentIds) =>
          set({ isMoveModalOpen: true, moveDocumentIds: documentIds }),

        closeMoveModal: () => set({ isMoveModalOpen: false, moveDocumentIds: [] }),

        openShareModal: (documentId) =>
          set({ isShareModalOpen: true, shareDocumentId: documentId }),

        closeShareModal: () => set({ isShareModalOpen: false, shareDocumentId: null }),

        // Cache actions
        setCachedFolders: (folders) => set({ cachedFolders: folders }),

        setRecentDocuments: (documents) => set({ recentDocuments: documents }),

        addToRecentDocuments: (document) =>
          set((state) => {
            const filtered = state.recentDocuments.filter((d) => d.id !== document.id);
            return {
              recentDocuments: [document, ...filtered].slice(0, 10), // Keep last 10
            };
          }),

        // Reset
        reset: () => set(initialState),
      }),
      {
        name: 'brightpath-documents-storage',
        partialize: (state) => ({
          viewMode: state.viewMode,
          expandedFolderIds: state.expandedFolderIds,
          sortBy: state.sortBy,
          sortOrder: state.sortOrder,
          recentDocuments: state.recentDocuments,
        }),
      }
    ),
    { name: 'DocumentsStore' }
  )
);

// -----------------------------------------------------------------------------
// Selectors
// -----------------------------------------------------------------------------

export const selectViewMode = (state: DocumentsState) => state.viewMode;
export const selectCurrentFolderId = (state: DocumentsState) => state.currentFolderId;
export const selectSelectedDocumentIds = (state: DocumentsState) => state.selectedDocumentIds;
export const selectExpandedFolderIds = (state: DocumentsState) => state.expandedFolderIds;
export const selectFilters = (state: DocumentsState) => state.filters;
export const selectSortBy = (state: DocumentsState) => state.sortBy;
export const selectSortOrder = (state: DocumentsState) => state.sortOrder;
export const selectUploadQueue = (state: DocumentsState) => state.uploadQueue;
export const selectIsUploading = (state: DocumentsState) => state.isUploading;
export const selectCachedFolders = (state: DocumentsState) => state.cachedFolders;
export const selectRecentDocuments = (state: DocumentsState) => state.recentDocuments;

export const selectHasSelection = (state: DocumentsState) =>
  state.selectedDocumentIds.length > 0;

export const selectSelectionCount = (state: DocumentsState) =>
  state.selectedDocumentIds.length;

export const selectUploadProgress = (state: DocumentsState) => {
  const queue = state.uploadQueue;
  if (queue.length === 0) return 0;
  const totalProgress = queue.reduce((sum, item) => sum + item.progress, 0);
  return Math.round(totalProgress / queue.length);
};

export const selectPendingUploads = (state: DocumentsState) =>
  state.uploadQueue.filter((item) => item.status === 'pending' || item.status === 'uploading');

export const selectCompletedUploads = (state: DocumentsState) =>
  state.uploadQueue.filter((item) => item.status === 'complete');

export const selectFailedUploads = (state: DocumentsState) =>
  state.uploadQueue.filter((item) => item.status === 'error');

export default useDocumentsStore;

